// Comments / Activity right panel — FE-043b parity v2 + Commitments-01c.
//
// Mirrors the "BeneCRM Tasks" Pencil frame: a 340px sidebar
// docked to the right of the Commitment detail page with a
// two-tab toggle (Comments | Activity) and a small badge for
// unread items. Comments is now wired through Commitments-01c —
// see ``CommitmentCommentsPanel``. Activity reads
// ``status_transitions`` off the detail payload today (later
// chunks fold in assignment + tag + visibility changes).
// Click-to-edit lives on the main column; this panel is read-only
// for activity, mutating-only for comments.

import { useMemo, useState } from "react";

import { CommitmentCommentsPanel } from "./CommitmentCommentsPanel";
import type {
  CommitmentDetail,
  CommitmentStatusTransition,
} from "./types";


export interface CommitmentDetailRightPanelProps {
  detail: CommitmentDetail;
}


type Tab = "comments" | "activity";


export function CommitmentDetailRightPanel({
  detail,
}: CommitmentDetailRightPanelProps) {
  // Comments is the primary surface — that's where conversations
  // (including ``@BeneAI`` invocations) happen. Activity is the
  // audit trail; useful but not the first thing the broker reaches
  // for when they open a Commitment.
  const [tab, setTab] = useState<Tab>("comments");

  const activityCount = detail.status_transitions.length;

  return (
    <aside
      data-testid="commitment-detail-right-panel"
      data-active-tab={tab}
      // Full-height side rail — matches the main column. Inner
      // body scrolls; the Comments compose row stays pinned to
      // the bottom on the Comments tab. ``lg:h-full`` so the
      // rail stretches with the page; ``min-h-0`` so the inner
      // overflow actually works.
      className="flex w-full flex-shrink-0 flex-col rounded-lg border border-border-subtle bg-raised lg:h-full lg:min-h-0 lg:w-[320px]"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-4 pt-4 pb-3">
        <div role="tablist" className="flex items-center gap-3">
          <TabButton
            active={tab === "comments"}
            label="Comments"
            testId="commitment-detail-tab-comments"
            onClick={() => setTab("comments")}
          />
          <TabButton
            active={tab === "activity"}
            label="Activity"
            testId="commitment-detail-tab-activity"
            onClick={() => setTab("activity")}
          />
        </div>
        {activityCount > 0 && (
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 rounded-full bg-ember"
            />
            <span
              data-testid="commitment-detail-activity-count"
              className="text-xs font-medium text-text-secondary"
            >
              {activityCount}
            </span>
          </div>
        )}
      </header>

      {tab === "comments" ? (
        <CommitmentCommentsPanel
          commitmentId={detail.commitment_id}
          privateScope={detail.visibility === "private"}
        />
      ) : (
        <ActivityTab transitions={detail.status_transitions} />
      )}
    </aside>
  );
}


function TabButton({
  active, label, testId, onClick,
}: {
  active: boolean;
  label: string;
  testId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={[
        "text-sm transition-fast ease-out",
        active
          ? "font-semibold text-text-primary"
          : "font-medium text-text-tertiary hover:text-text-primary",
      ].join(" ")}
    >
      {label}
    </button>
  );
}


function ActivityTab({
  transitions,
}: { transitions: CommitmentStatusTransition[] }) {
  const events = useMemo(() => {
    return transitions.slice().sort((a, b) => {
      const aT = a.entered_at ? new Date(a.entered_at).getTime() : 0;
      const bT = b.entered_at ? new Date(b.entered_at).getTime() : 0;
      return bT - aT;
    });
  }, [transitions]);

  return (
    <ul
      role="list"
      data-testid="commitment-detail-activity-tab"
      className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-4 py-3 text-sm"
    >
      {events.length === 0 ? (
        <li className="text-center text-xs text-text-tertiary">
          No activity yet.
        </li>
      ) : (
        events.map((t) => (
          <li
            key={t.transition_id}
            className="flex items-start gap-2"
          >
            <span
              aria-hidden="true"
              className="mt-1 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sage-tint text-[10px] font-semibold text-teal"
            >
              {t.actor_kind?.slice(0, 2).toUpperCase() ?? "—"}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs leading-snug text-text-primary">
                Status changed{" "}
                <span className="font-medium">
                  {t.from_status ?? "—"} → {t.to_status}
                </span>
              </span>
              {t.entered_at && (
                <span className="text-[11px] text-text-tertiary">
                  {formatTs(t.entered_at)}
                </span>
              )}
            </div>
          </li>
        ))
      )}
    </ul>
  );
}


function formatTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
